import React, { type ReactElement } from 'react';
import { ErrorMessage } from '@hookform/error-message';
import { lowerFirst } from 'lodash';
import type { FieldErrors, FieldPath, FieldValues } from 'react-hook-form';
import ErrorMessagePresenter from 'atom-components/error-message';
import styles from './index.module.scss';

type ErrorMessageProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  errors: FieldErrors<TFieldValues>;
  name: TName;
  prefix?: string;
  containerClassName?: string;
  className?: string;
};

export default function ErrorMessageFromHookForm({
  errors,
  name,
  prefix,
  containerClassName,
  className
}: ErrorMessageProps): ReactElement {
  return (
    <ErrorMessage
      errors={errors}
      name={name}
      render={({ message }): ReactElement => (
        <ErrorMessagePresenter containerClassName={containerClassName} className={className}>
          <>{prefix ? `${prefix} ${lowerFirst(message)}` : message}</>
        </ErrorMessagePresenter>
      )}
    />
  );
}

type ErrorMessageRangeProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  errors: FieldErrors<TFieldValues>;
  fromName: TName;
  toName: TName;
};

export function ErrorMessagesForRange({
  errors,
  fromName,
  toName
}: ErrorMessageRangeProps): ReactElement | null {
  if (!errors[fromName] && !errors[toName]) return null;

  return (
    <div className={styles.errorMessagesForRange}>
      {errors[fromName] && (
        <ErrorMessageFromHookForm errors={errors} name={fromName} prefix="From value" />
      )}
      {errors[toName] && (
        <ErrorMessageFromHookForm errors={errors} name={toName} prefix="To value" />
      )}
    </div>
  );
}
